import {createSlice, Dispatch, PayloadAction} from '@reduxjs/toolkit';
import Modules from '@/common/modules';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from './store-config';
import {useMemo} from 'react';
import AuthUtil from '@/util/auth-util';
import ApiAuth from '@/api/api-auth';
import LoadState from '@/common/load-state';
import MessageUtil from '@/util/message-util';

export interface TokenType {
    access: {
        token: string;
        expire: number;
    };
    refresh: {
        token: string;
        expire: number;
    };
}

export interface UserInfo {
    tokens: TokenType;
    sequence?: number;
}

export interface AuthState {
    userLoadState: LoadState;
    user?: UserInfo;
}

const initialState: AuthState = {
    userLoadState: LoadState.INIT,
    user: undefined,
};

const isAccessExpire = (expire: number) => {
    const now = Math.floor(new Date().getTime() / 1000);
    return Number(expire || 0) < now + 60 * 10;
};

const isRefreshExpire = (expire: number) => {
    const now = Math.floor(new Date().getTime() / 1000);
    return Number(expire || 0) < now + 10;
};

const slice = createSlice({
    name: Modules.AUTH,
    initialState,
    reducers: {
        setUserInfo: (state, action: PayloadAction<UserInfo | undefined>) => {
            state.user = action.payload;
            AuthUtil.setCacheUserInfo(state.user);
        },
        setUserInfoInner: (state, action: PayloadAction<{state: LoadState; user?: UserInfo}>) => {
            state.user = action.payload.user;
            state.userLoadState = action.payload.state;
            AuthUtil.setCacheUserInfo(state.user);
        },
    },
});

export const useAuthState = () => {
    return useSelector((state: RootState) => state.auth) as AuthState;
};

export const useAuthAction = () => {
    const dispatch = useDispatch();
    return useMemo(
        () => ({
            setUserInfo: (user?: UserInfo) => dispatch(slice.actions.setUserInfo(user)),
        }),
        [dispatch],
    );
};

export const useAuthLoader = () => {
    const {setUserInfo} = useAuthAction();
    AuthUtil.setSliceUserInfoCallback(setUserInfo);
    return null;
};

const setUserInfoInner = (dispatch: Dispatch<any>, state: LoadState, tokens?: TokenType) => {
    dispatch(
        slice.actions.setUserInfoInner({
            user: tokens
                ? {
                      tokens,
                  }
                : undefined,
            state,
        }),
    );
};

export const useLoadUserInfo = () => {
    const dispatch = useDispatch();
    return useMemo(
        () => ({
            loadUserInfo: async () => {
                setUserInfoInner(dispatch, LoadState.LOADING);

                const localTokens: TokenType | undefined = AuthUtil.getStoreUserToken();
                const access = localTokens?.access;
                if (access && access.token && !isAccessExpire(access.expire)) {
                    setUserInfoInner(dispatch, LoadState.LOADED, localTokens);
                    return;
                }

                const refresh = localTokens?.refresh;
                if (refresh && refresh.token && !isRefreshExpire(refresh.expire)) {
                    const response: any = await ApiAuth.refreshToken({
                        token: refresh.token,
                    });
                    if (response?.status === 401) {
                        AuthUtil.setStoreUserToken(undefined);
                        setUserInfoInner(dispatch, LoadState.LOADED);
                    } else {
                        if (response?.data?.code === 0) {
                            const tokens = response.data.data as TokenType;
                            AuthUtil.setStoreUserToken(tokens);
                            setUserInfoInner(dispatch, LoadState.LOADED, tokens);
                        } else {
                            MessageUtil.error(response?.data?.msg || '网络请求失败');
                            setUserInfoInner(dispatch, LoadState.FAILED);
                        }
                    }
                    return;
                }

                AuthUtil.setStoreUserToken(undefined);
                setUserInfoInner(dispatch, LoadState.LOADED);
            },
            clearUserInfo: () => {
                AuthUtil.setStoreUserToken(undefined);
                setUserInfoInner(dispatch, LoadState.LOADED);
            },
            loadUserInfoWithTicket: async (ticket: string) => {
                if (!ticket) {
                    MessageUtil.error('认证票据参数缺失');
                    setUserInfoInner(dispatch, LoadState.FAILED);
                    return;
                }

                setUserInfoInner(dispatch, LoadState.LOADING);
                const response: any = await ApiAuth.getToken({
                    code: AuthUtil.getAppCode(),
                    ticket,
                });
                if (response?.data?.code === 0) {
                    const tokens = response.data.data as TokenType;
                    AuthUtil.setStoreUserToken(tokens);
                    setUserInfoInner(dispatch, LoadState.LOADED, tokens);
                } else {
                    MessageUtil.error(response?.data?.msg || '网络请求失败');
                    setUserInfoInner(dispatch, LoadState.FAILED);
                }
            },
        }),
        [dispatch],
    );
};

export default slice.reducer;
